import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { Observable, Subscription } from 'rxjs';

import * as filterHelpers from 'app/modules/browse/helpers/filter.helpers';
import { ModalService } from 'app/services/modal.service';
import { ModeNamesEnum } from '../../models/browse-mode';
import {
  BrowseFilter,
  FilterNamesEnum,
  getAllFiltersInSearchMode,
  getAvailableFilters,
  getFiltersCopy,
} from '../../models/filter';
import { FilterOption } from '../../models/filter-options';
import { Sport } from '../../models/sport';
import { FilterStore } from '../../store/filter/filter.store';
import { SportStore } from '../../store/sport/sport.store';
import { TranslateModule } from '@ngx-translate/core';
import { AllFiltersFieldComponent } from './all-filters-field/all-filters-field.component';
import { AsyncPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import { Unit } from '@kinomap-dev/ng-ui-lib';
import { selectMeUnit } from 'app/store/me/me.selectors';

@Component({
  selector: 'app-all-filters',
  templateUrl: './all-filters.component.html',
  styleUrls: ['./all-filters.component.scss'],
  standalone: true,
  imports: [AllFiltersFieldComponent, AsyncPipe, TranslateModule],
})
export class AllFiltersComponent {
  private cd: ChangeDetectorRef = inject(ChangeDetectorRef);
  private modalService: ModalService = inject(ModalService);
  private filterStore: FilterStore = inject(FilterStore);
  private sportStore: SportStore = inject(SportStore);
  private store: Store = inject(Store);
  private subscriptions: Subscription[] = [];

  filters: BrowseFilter[] = [];
  selectedSport$: Observable<Sport> = this.sportStore.selectedSport$;
  filterOptionsPairs: {
    filterName: FilterNamesEnum;
    selectedOptions: FilterOption[];
  }[] = [];
  isSearch: boolean = false;
  isMap: boolean = false;
  modeName: ModeNamesEnum | null = null;
  isFilteringEnabled: boolean = this.hasAnyOptionChanged();
  filtersCleared: boolean = false;
  unit: Observable<Unit> = this.store.select(selectMeUnit);

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
  }

  reset(
    {
      modeName,
      isSearch,
      isMap,
    }: {
      modeName?: ModeNamesEnum;
      isSearch?: boolean;
      isMap?: boolean;
    } = { isSearch: false, modeName: null }
  ) {
    this.subscriptions.forEach((sub: Subscription) => sub.unsubscribe());
    this.modeName = modeName;
    this.isSearch = isSearch;
    this.isMap = isMap;
    this.filters = [];
    this.filterOptionsPairs = [];
    this.isFilteringEnabled = this.hasAnyOptionChanged();
    this.cd.detectChanges();
    this.subscriptions = [this.subscriptionToFilters()];
  }

  subscriptionToFilters(): Subscription {
    if (this.isMap) {
      return this.filterStore.mapFilters$.subscribe(
        (filters: BrowseFilter[]) =>
          (this.filters = filters.filter(
            (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
          ))
      );
    }

    if (this.isSearch) {
      return this.filterStore.searchedContentFilters$.subscribe(
        (filters: BrowseFilter[]) =>
          (this.filters = filters.filter(
            (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
          ))
      );
    }

    switch (this.modeName) {
      case ModeNamesEnum.Videos:
        return this.filterStore.videoFilters$.subscribe(
          (filters: BrowseFilter[]) =>
            (this.filters = filters.filter(
              (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
            ))
        );

      case ModeNamesEnum.Gps:
        return this.filterStore.gpsFilters$.subscribe(
          (filters: BrowseFilter[]) =>
            (this.filters = filters.filter(
              (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
            ))
        );

      case ModeNamesEnum.Workouts:
        return this.filterStore.workoutFilters$.subscribe(
          (filters: BrowseFilter[]) =>
            (this.filters = filters.filter(
              (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
            ))
        );

      case ModeNamesEnum.Coachings:
        return this.filterStore.coachingFilters$.subscribe(
          (filters: BrowseFilter[]) =>
            (this.filters = filters.filter(
              (f: BrowseFilter) => f.name !== FilterNamesEnum.SortDirection
            ))
        );
    }
  }

  onChangeSelectedOptions(event: {
    filterName: FilterNamesEnum;
    selectedOptions: FilterOption[];
  }): void {
    if (event.filterName === FilterNamesEnum.SearchMode) {
      const previousSearchMode: string = this.filters.find(
        (f) => f.name === FilterNamesEnum.SearchMode
      ).selectedOptions[0].value;
      const hasSearchModeChanged: boolean =
        previousSearchMode !== event.selectedOptions[0].value;

      if (hasSearchModeChanged) {
        const newModeFilter: BrowseFilter = getFiltersCopy().find(
          (f) => f.name === FilterNamesEnum.SearchMode
        );
        newModeFilter.selectedOptions = [...event.selectedOptions];
        const availableFilters = getAllFiltersInSearchMode(newModeFilter);
        this.filterStore.setSearchedContentFilters(availableFilters);
        this.reset({ isSearch: true });
        return;
      }
    }

    const filterOptionsPair = this.filterOptionsPairs.find(
      (pair) => pair.filterName === event.filterName
    );

    if (!filterOptionsPair) {
      this.filterOptionsPairs.push(event);
      return;
    }

    filterOptionsPair.selectedOptions = event.selectedOptions;

    this.isFilteringEnabled = this.hasAnyOptionChanged();
    this.cd.detectChanges();
  }

  onApplyFilters(): void {
    let availableFilters: BrowseFilter[] = [];
    if (this.isSearch && !this.isMap) {
      const modeOption: FilterOption = this.filterOptionsPairs.find(
        (fop) => fop.filterName === FilterNamesEnum.SearchMode
      ).selectedOptions[0];
      const newModeFilter: BrowseFilter = getFiltersCopy().find(
        (f) => f.name === FilterNamesEnum.SearchMode
      );
      newModeFilter.selectedOptions = [modeOption];
      availableFilters = getAllFiltersInSearchMode(newModeFilter);
    } else {
      availableFilters = getAvailableFilters({
        modeName: this.modeName,
      });
    }

    for (const filterOptionsPair of this.filterOptionsPairs) {
      const filter: BrowseFilter = availableFilters.find(
        (f) => f.name === filterOptionsPair.filterName
      );

      if (!!filter) {
        filter.selectedOptions = filterOptionsPair.selectedOptions;
      }
    }

    if (this.isMap) {
      this.filterStore.setMapFilters(availableFilters);
    } else if (this.isSearch) {
      this.filterStore.setSearchedContentFilters(availableFilters);
    } else {
      switch (this.modeName) {
        case ModeNamesEnum.Videos:
          this.filterStore.setVideoFilters(availableFilters);
          break;
        case ModeNamesEnum.Gps:
          this.filterStore.setGpsFilters(availableFilters);
          break;
        case ModeNamesEnum.Workouts:
          this.filterStore.setWorkoutFilters(availableFilters);
          break;
        case ModeNamesEnum.Coachings:
          this.filterStore.setCoachingFilters(availableFilters);
          break;
      }
    }

    this.modalService.hide();
  }

  onClearFilters(): void {
    if (
      this.filterOptionsPairs.map((obj) => obj.selectedOptions).flat()
        .length === 0
    ) {
      return;
    }

    this.filtersCleared = true;
    this.cd.detectChanges();
    this.filtersCleared = false;
  }

  hasAnyOptionChanged(): boolean {
    return this.filters.some((filter) => {
      const matchingPair = this.filterOptionsPairs.find(
        (pair) => pair.filterName === filter.name
      );
      if (!matchingPair) {
        return true;
      }
      return filterHelpers.haveOptionsChanged(
        filter,
        matchingPair.selectedOptions
      );
    });
  }
}
